import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, CheckCircle2, AlertCircle, TriangleAlert, Mail, AlertCircleIcon } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { validateEmail } from 'helpers';
import { Link } from 'react-router-dom';
import { Urls } from 'data';
import { useAPI } from 'hooks';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Button } from 'components/ui/button';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { AuthCard, AuthPageLayout } from 'components/shared/AuthPageLayout';

const Register = () => {
  const [showSuccessCard, setShowSuccessCard] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [captcha, setCaptcha] = useState(null);

  const { api } = useAPI();
  const queryClient = useQueryClient();
  const isMutating = queryClient.isMutating() > 0;

  const validateName = (value) => value.trim().length >= 1;

  const validatePhoneNumber = (value) => /^(\+229)?01[0-9]{8}$/.test(value);

  const { mutate: registerPilgrim } = useMutation({
    mutationFn: () => {
      const payload = {
        admin_last_name: lastName.trim(),
        admin_first_name: firstName.trim(),
        phone: phoneNumber,
        admin_email: email.trim(),
        captcha,
      };
      return api.post('pilgrim_org/', payload);
    },
    onSuccess: () => {
      setError('');
      setSuccessMessage('Inscription réussie !');
      setShowSuccessCard(true);
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!validateName(lastName)) {
      setError('Le nom est requis.');
      return;
    }
    if (!validateName(firstName)) {
      setError('Le prénom est requis.');
      return;
    }
    if (!validatePhoneNumber(phoneNumber)) {
      setError('Le numéro de téléphone doit commencer par 01 et contenir 10 chiffres (01 XX XX XX XX).');
      return;
    }
    if (email && !validateEmail(email)) {
      setError("L'email fourni n'est pas valide.");
      return;
    }
    if (!captcha) {
      setError('Veuillez compléter le CAPTCHA.');
      return;
    }

    registerPilgrim();
  };

  const isDisabled = isMutating || !lastName || !firstName || !email || !phoneNumber || !captcha;

  return (
    <AuthPageLayout>
      {showSuccessCard && (
        <AuthCard className="max-w-[488px]">
          <div className="mb-[26px]">
            <div className="mb-6 grid h-12 w-12 place-items-center rounded-xl bg-[#edf5ef] text-hajj-primary">
              <Mail className="h-[25px] w-[25px]" aria-hidden="true" />
            </div>
            <h1 className="text-[26px] font-semibold leading-[1.3] tracking-[-0.55px] text-[#21312a] max-[520px]:text-2xl">
              Compte créé 🎉
            </h1>
            <p className="mt-[13px] max-w-[365px] text-sm leading-[1.7] text-[#62736a]">
              Il ne reste plus qu&#39;une étape : vérifiez votre e-mail pour activer votre compte.
            </p>
          </div>

          <section className="border-t border-[#e5ebe6] pt-[22px]">
            <h2 className="mb-[18px] text-sm font-semibold text-[#344e3c]">Prochaines étapes</h2>
            <ol className="space-y-[13px] text-sm leading-[1.6] text-[#536b5b]">
              {[
                'Consultez votre boîte de réception (et vos spams)',
                "Cliquez sur le lien de vérification dans l'e-mail",
                'Connectez-vous à votre compte pour commencer',
              ].map((step, index) => (
                <li key={step} className="flex items-baseline gap-3.5">
                  <span className="min-w-[19px] text-xs font-semibold tabular-nums text-[#64836e]">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </section>
        </AuthCard>
      )}

      {!showSuccessCard && (
        <AuthCard className="max-w-[500px]">
          <div className="mb-[25px] text-left">
            <h1 className="text-2xl font-semibold leading-[1.3] tracking-[-0.55px] text-[#21312a] max-[520px]:text-[22px]">
              Inscription
            </h1>
            <p className="mt-2 text-sm leading-[1.6] text-[#62736a]">Inscrivez-vous et activez votre compte</p>
          </div>

          {error && (
            <Alert variant="destructive" className="animate-slide-up flex items-center gap-1 mb-4">
              <TriangleAlert className="h-4 w-4 text-red-600 mb-2" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {successMessage && (
            <Alert className="border-green-500 bg-green-50 text-green-900 animate-slide-up">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription>{successMessage}</AlertDescription>
            </Alert>
          )}

          <Alert className="mb-[21px] border-[#edf0ec] bg-[#fafbf9] p-[10px] text-[#64756a]">
            <div className="flex items-center gap-2">
              <AlertCircleIcon className="h-3.5 w-3.5 shrink-0 text-[#819588]" aria-hidden="true" />
              <AlertTitle className="mb-0 text-[11.5px] font-normal leading-[1.55] tracking-normal">
                Vous devez avoir au moins 18 ans pour vous inscrire. En validant votre inscription, vous déclarez être
                majeur et accepter{' '}
                <a
                  href={Urls.cgu}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-hajj-primary hover:text-hajj-secondary font-medium transition-colors"
                >
                  les conditions d&apos;utilisation.
                </a>
              </AlertTitle>
            </div>
          </Alert>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email <span className="text-red-600">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                variant="hajj"
                size="sm"
              />
              <p className="mt-1.5 flex items-start gap-1 text-xs leading-[1.5] text-[#637469]">
                <span className="flex gap-1">
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                  Renseigner une adresse e-mail valide pour activer votre compte.
                </span>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">
                  Nom *
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Entrez votre nom"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setError('');
                  }}
                  variant="hajj"
                  size="sm"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">
                  Prénom *
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Entrez votre prénom"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setError('');
                  }}
                  variant="hajj"
                  size="sm"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="phoneNumber" className="text-sm font-medium text-gray-700">
                Numéro de téléphone *
              </Label>
              <Input
                id="phoneNumber"
                type="tel"
                maxLength={10}
                placeholder="01 XX XX XX XX"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  setError('');
                }}
                variant="hajj"
                size="sm"
              />
            </div>

            <div className="w-full max-w-full">
              <ReCAPTCHA sitekey={process.env.REACT_APP_CAPTCHA_PUBLIC_KEY} onChange={(value) => setCaptcha(value)} />
            </div>

            <Button type="submit" variant="hajj" className="w-full py-[20px]" size="sm" disabled={isDisabled}>
              {isMutating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Inscription en cours...
                </>
              ) : (
                "S'inscrire"
              )}
            </Button>

            <div className="pt-2 text-center text-sm text-[#65766c]">
              Vous avez déjà un compte ?{' '}
              <Link
                to={Urls.front.login}
                className="inline-flex items-center text-sm text-hajj-primary hover:text-hajj-secondary font-medium transition-colors"
              >
                Connectez-vous !
              </Link>
            </div>
          </form>
        </AuthCard>
      )}
    </AuthPageLayout>
  );
};

export default Register;
